import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DoneIcon from "@mui/icons-material/Done";
import CheckIcon from "@mui/icons-material/Check";
import PreviewIcon from "@mui/icons-material/Preview";
import MediaPicker from "../../mediaPicker/MediaPicker";
import { colorStyling } from "../../../../helpers/color";
import {
  uploadMedia,
  setErrorHandler,
} from "../../../../store/actions/cmsAction";
import {
  getImageSource,
  splitFileName,
} from "../../../../helpers/fileProcessing";
import { logger } from "../../../../helpers/logger";
import {
  TypographyLarge,
  TypographyNormal,
  TypographySmall,
} from "../../../customComponent";
import parser from "html-react-parser";
import { v4 as uuidv4 } from "uuid";
import "../styles.min.css";

import dayjs from "dayjs";
import UploadErrorDialog from "../../mediaPicker/UploadErrorDialog";
import { SET_ERROR_PROMPT_CMS_PAGE } from "../../../../store/actionTypes/cmsActionType";
const timezone = require("dayjs/plugin/timezone");
dayjs.extend(timezone);

const assetMaxSizeLimit =
  (process.env.REACT_APP_ASSET_MAX_SIZE_LIMIT || 300) * 1024 * 1024; // 300MB

const AdslotSetting = ({
  open,
  handleClose,
  adslot,
  type,
  handleUpdate,
  setErrors,
  container,
}) => {
  const dispatch = useDispatch();
  const { uploadingMedia, allowedAssetFiles } = useSelector(
    (state) => state.cms
  );

  const { t } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [checkboxEndDate, setCheckboxEndDate] = useState(false);
  const [errOnDateInput, setErrOnDateInput] = useState(false);
  const [errNoStartDate, setErrNoStartDate] = useState(false);
  const [errNoEndDate, setErrNoEndDate] = useState(false);

  const [adPlaceholderCheck, setAdPlaceholderCheck] = useState(true);
  const [adPlaceholderFile, setAdPlaceholderFile] = useState({});
  const [openMediaPicker, setOpenMediaPicker] = useState(false);
  const [noPlaceholderCheckbox, setNoPlaceholderCheckbox] = useState(false);
  const [noPlaceholderAnimating, setNoPlaceholderAnimating] = useState(false);
  const [noPlaceholderAnimStart, setNoPlaceholderAnimStart] = useState(false);
  const [errNoPlaceholder, setErrNoPlaceholder] = useState(false);
  const [errNotUploaded, setErrNotUploaded] = useState(false);

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);
  const [fileErrorMessages, setFileErrorMessages] = useState([]);
  const [errUploadPlaceholder, setErrUploadPlaceholder] = useState(false);

  const [adPlaceholder, setAdPlaceholder] = useState({});
  const [showThumbnailButton, setShowThumbnailButton] = useState(false);
  const [showThumbnailFull, setShowThumbnailFull] = useState(false);
  const [disableButtonFlag, setDisableButtonFlag] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [editedFlag, setEditedFlag] = useState(false);

  const [anchorMenuEl, setAnchorMenuEl] = useState(null);
  const openMenu = Boolean(anchorMenuEl);
  const handleOpenMenu = (event) => {
    setAnchorMenuEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorMenuEl(null);
  };

  useEffect(() => {
    if (!errorDialogOpen) {
      setFileErrors([]);
      setFileErrorMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorDialogOpen]);

  const handleCancelDialog = () => {
    if (editedFlag) {
      setOpenCancelDialog(true);
    } else {
      handleClose();
    }
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
  };

  useEffect(() => {
    if (open) {
      switch (type) {
        default:
          break;
        case "date":
          if (adslot.startAt?.length > 0)
            setStartDate(dayjs(adslot.startAt).format("YYYY-MM-DD"));
          if (adslot.endAt?.length > 0)
            setEndDate(
              adslot.endAt ? dayjs(adslot.endAt).format("YYYY-MM-DD") : ""
            );
          setCheckboxEndDate(adslot.endAt ? true : false);
          break;
        case "placeholder":
          if (adslot.placeholder?.id) {
            setAdPlaceholderCheck(true);
            setAdPlaceholder(adslot.placeholder);
            setAdPlaceholderFile(adslot.placeholder);
            setShowThumbnailButton(true);
          } else {
            setAdPlaceholder({});
          }
          break;
      }
    }

    return () => {
      setStartDate("");
      setEndDate("");
      setCheckboxEndDate(false);
      setErrNoStartDate(false);
      setErrNoEndDate(false);
      setErrOnDateInput(false);

      setAdPlaceholderFile({});
      setShowThumbnailButton(false);
      setAdPlaceholderCheck(false);
      setOpenCancelDialog(false);
      setEditedFlag(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleStartDate = (e) => {
    setStartDate(e.target.value);
    setEndDate(dayjs(e.target.value).add(1, "d").format("YYYY-MM-DD"));
    setErrNoStartDate(false);
    setErrNoEndDate(false);
    setEditedFlag(true);
  };

  const handleEndDate = (e) => {
    setEndDate(e.target.value);
    setErrNoEndDate(false);
    setEditedFlag(true);
  };

  const handleCheckboxEndDate = (checked) => {
    if (checked) {
      if (startDate?.length > 0)
        setEndDate(dayjs(startDate).add(1, "d").format("YYYY-MM-DD"));
    } else {
      setEndDate("");
      setErrNoEndDate(false);
    }
    setCheckboxEndDate(checked);
    setEditedFlag(true);
  };

  const handleOpenMediaPicker = () => {
    setOpenMediaPicker(true);
  };

  const handleCloseMediaPicker = () => {
    setOpenMediaPicker(false);
  };

  const handleAdPlaceholderCheck = (value) => {
    setAdPlaceholderCheck(value);
    setErrNoPlaceholder(false);
    setErrNotUploaded(false);
    setErrUploadPlaceholder(false);
    setEditedFlag(true);
  };

  const handleAdPlaceholderFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const errorFiles = [];
    const errorMessages = [];
    if (file.size > assetMaxSizeLimit) {
      errorFiles.push(file);
      errorMessages.push(t("uploadError.errSizeExceedLimit"));
      setFileErrors(errorFiles);
      setFileErrorMessages(errorMessages);
      setErrorDialogOpen(true);
      return;
    }

    setShowThumbnailButton(false);
    setErrNoPlaceholder(false);
    setErrNotUploaded(false);
    setErrUploadPlaceholder(false);
    setAdPlaceholder({});
    setAdPlaceholderFile(file);
    setEditedFlag(true);
  };

  const uploadNewMedia = async () => {
    setDisableButtonFlag(true);
    const payload = [];
    logger.log(`UPLOADED:`, { file: adPlaceholderFile });
    if (!adPlaceholderFile.name) {
      return;
    }

    const fileNameSplit = splitFileName(adPlaceholderFile.name);
    const inputProps = {
      ...fileNameSplit,
      type: adPlaceholderFile.type,
    };

    await getImageSource(
      { input: adPlaceholderFile, inputProps },
      allowedAssetFiles
    ).then(
      ({ type, url, dimension, videoURL, localURL }) => {
        const placeholder = {
          url,
          id: uuidv4(),
          file: adPlaceholderFile,
          fileSrc: url,
          videoSrc: videoURL,
          type: type,
          name: adPlaceholderFile.name,
          dimension: dimension,
          size: adPlaceholderFile.size,
        };

        setAdPlaceholder(placeholder);
        payload.push(placeholder);

        dispatch(
          uploadMedia(payload, (res) => {
            if (res.length > 0) {
              setAdPlaceholder(res[0]);
              setErrNoPlaceholder(false);
              setErrNotUploaded(false);
              setErrUploadPlaceholder(false);
              setTimeout(() => {
                setShowThumbnailButton(true);
                setDisableButtonFlag(false);
              }, 1234);
            }
          })
        );
      },
      ({ file, message }) => {
        logger.error(`ERROR:`, message);
        setErrUploadPlaceholder(true);
        setDisableButtonFlag(false);
      }
    );
  };

  const setupAdPlaceholder = (data, order) => {
    if (order.length === 0) {
      dispatch(setErrorHandler({ selectedMedia: true }));
      return;
    }

    let placeholderFile;
    for (let i = 0; i < order.length; i++) {
      placeholderFile = data[order[i]];
      logger.log(`setupAdPlaceholder:`, placeholderFile);
      setAdPlaceholderFile(placeholderFile);
    }

    handleCloseMediaPicker();
    setShowThumbnailButton(true);
    setErrNoPlaceholder(false);
    setErrNotUploaded(false);
    setErrUploadPlaceholder(false);
    setAdPlaceholder(placeholderFile);
    setEditedFlag(true);
  };

  const submitDate = () => {
    let errInput = false;
    if (startDate?.length === 0) {
      setErrNoStartDate(true);
      errInput = true;
    }
    if (checkboxEndDate && endDate?.length === 0) {
      setErrNoEndDate(true);
      errInput = true;
    }
    if (errInput) {
      setErrOnDateInput(true);
      return false;
    }

    const payload = {};
    payload.startAt = dayjs(startDate)?.toISOString();
    if (checkboxEndDate)
      payload.endAt = dayjs(endDate)
        .add(23, "hour")
        .add(59, "minute")
        .toISOString();
    else payload.endAt = null;
    handleUpdate(adslot.index, "date", payload);
    return true;
  };

  const submitPlaceholder = () => {
    let placeholder;
    if (adPlaceholderCheck) {
      placeholder = adPlaceholder.response ?? adPlaceholder;
      if (adPlaceholderFile.name && !placeholder.id) {
        setErrNotUploaded(true);
        return false;
      }
      if (!placeholder?.name) {
        setErrNoPlaceholder(true);
        return false;
      }
    } else {
      placeholder = {};
    }
    handleUpdate(adslot.index, "placeholder", placeholder);
    return true;
  };

  const submit = () => {
    let success;
    switch (type) {
      default:
        break;
      case "date":
        success = submitDate();
        break;
      case "placeholder":
        if (!adPlaceholderCheck && !noPlaceholderCheckbox) {
          setNoPlaceholderAnimStart(true);
          return;
        }

        success = submitPlaceholder();
        if (!success) {
          setErrNoPlaceholder(true);
        }
        break;
    }
    if (success) {
      handleClose();
      setErrors({});
      dispatch({ type: SET_ERROR_PROMPT_CMS_PAGE, payload: false });
    }
  };

  const previewPlaceholder = useMemo(() => {
    if (!adPlaceholder?.name) {
      return null;
    }

    let placeholder = adPlaceholder;
    if (!placeholder.url && placeholder.response) {
      placeholder = placeholder.response;
    }

    const placeholderURL =
      placeholder.vidSrc ?? placeholder.url ?? placeholder.src;
    switch (placeholder.type) {
      case "VIDEO":
        return (
          <video
            is="x-muted"
            draggable={false}
            src={placeholderURL}
            alt={placeholder.name}
            controls={true}
            onClick={(e) => e.stopPropagation()}
            style={{
              minHeight: "100%",
              objectFit: "contain",
            }}
          />
        );

      case "image":
      default:
        return (
          <img
            draggable={false}
            src={placeholderURL}
            alt={t("contentImageAltText")}
            onClick={(e) => e.stopPropagation()}
            style={{
              objectFit: "contain",
            }}
          />
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adPlaceholder]);

  useEffect(() => {
    let intervalId;

    if (noPlaceholderAnimStart) {
      // Toggle the state every 500ms (0.5 seconds) to create the blink effect
      intervalId = setInterval(() => {
        setNoPlaceholderAnimating((prev) => !prev);
      }, 500);

      // Stop the animation after a few cycles (e.g., after 3 seconds)
      setTimeout(() => {
        clearInterval(intervalId);
        setNoPlaceholderAnimating(false);
        setNoPlaceholderAnimStart(false);
      }, 3000);
    }

    return () => clearInterval(intervalId);
  }, [noPlaceholderAnimStart]);

  return (
    <>
      <Dialog maxWidth="sm" fullWidth open={open}>
        <DialogTitle
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          {type === "date" && t("setting.adslotDateTitle")}
          {type === "placeholder" && t("setting.adslotPlaceholderTitle")}
        </DialogTitle>

        {type === "date" && (
          <DialogContent>
            <Box
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"space-between"}
            >
              <Box display={"flex"} flexDirection={"column"} gap={1}>
                <Box display={"flex"} sx={{ alignItems: "end" }}>
                  <Box sx={{ width: "100%", mb: 0 }}>
                    <InputLabel sx={{ mr: 0.5 }}>
                      {t("setting.adslotDateStartAtLabel")}
                    </InputLabel>

                    <TextField
                      size="small"
                      fullWidth
                      value={startDate}
                      onChange={handleStartDate}
                      required
                      margin="dense"
                      type="date"
                      variant="outlined"
                      InputProps={{
                        inputProps: {
                          min: dayjs().format("YYYY-MM-DDT00:00"),
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      ml: 2,
                    }}
                  >
                    <Box sx={{ pb: checkboxEndDate ? 0 : 0.5 }}>
                      <FormControlLabel
                        control={<Checkbox />}
                        value={checkboxEndDate}
                        checked={checkboxEndDate}
                        name={"showEndDate"}
                        onChange={(e) => {
                          handleCheckboxEndDate(e.target.checked);
                        }}
                        label={t("setting.adslotDateEndAtCheckboxLabel")}
                      />
                    </Box>

                    {checkboxEndDate && (
                      <Box sx={{ width: "100%" }}>
                        <InputLabel>
                          {t("setting.adslotDateEndAtLabel")}
                        </InputLabel>

                        <TextField
                          size="small"
                          fullWidth
                          value={endDate}
                          onChange={handleEndDate}
                          margin="dense"
                          type="date"
                          variant="outlined"
                          InputProps={{
                            inputProps: {
                              min: startDate,
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                {errOnDateInput && (
                  <Box display={"flex"} flexDirection={"column"} gap={"2px"}>
                    {errNoStartDate && (
                      <Box>
                        <TypographySmall
                          sx={{
                            ml: 1,
                            mt: "2px",
                            color: "red",
                            fontWeight: 600,
                          }}
                        >
                          {t("setting.errAdslotDateStartAtEmpty")}
                        </TypographySmall>
                      </Box>
                    )}
                    {errNoEndDate && (
                      <Box>
                        <TypographySmall
                          sx={{
                            ml: 1,
                            mt: "2px",
                            color: "red",
                            fontWeight: 600,
                          }}
                        >
                          {t("setting.errAdslotDateEndAtEmpty")}
                        </TypographySmall>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
        )}

        {type === "placeholder" && (
          <DialogContent>
            <UploadErrorDialog
              open={errorDialogOpen}
              onClose={() => setErrorDialogOpen(false)}
              files={fileErrors}
              errors={fileErrorMessages}
            />

            <MediaPicker
              open={openMediaPicker}
              handleClose={handleCloseMediaPicker}
              name={``}
              addContent={setupAdPlaceholder}
              container={container}
              contentState={[null]}
              onlyOne={true}
              noUpload={true}
            />

            <Box
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"space-between"}
            >
              <Box display={"flex"} flexDirection={"column"} sx={{ mb: 1 }}>
                <Box display={"flex"} alignItems={"center"}>
                  <Checkbox
                    value={adPlaceholderCheck}
                    checked={adPlaceholderCheck}
                    onClick={() =>
                      handleAdPlaceholderCheck(!adPlaceholderCheck)
                    }
                    sx={{ padding: "4px" }}
                  />
                  <InputLabel
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      handleAdPlaceholderCheck(!adPlaceholderCheck)
                    }
                  >
                    <TypographyLarge>
                      {parser(t("setting.placeholderCheckbox"))}
                    </TypographyLarge>
                  </InputLabel>
                </Box>

                {adPlaceholderCheck && (
                  <Box display={"flex"} alignItems={"center"}>
                    <input
                      accept="image/*,video/*"
                      style={{ display: "none" }}
                      id="file-upload"
                      type="file"
                      onChange={handleAdPlaceholderFileChange}
                    />
                    <Box sx={{ ml: 2 }}>
                      <Button
                        variant="contained"
                        component="span"
                        onClick={handleOpenMenu}
                        sx={{
                          mr: 1,
                          "&:hover": {
                            backgroundColor: "#0093ff",
                          },
                        }}
                      >
                        {tCommon("chooseBtn")}
                      </Button>
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorMenuEl}
                        open={openMenu}
                        onClose={handleCloseMenu}
                        elevation={3}
                        anchorOrigin={{
                          horizontal: "left",
                          vertical: "bottom",
                        }}
                        transformOrigin={{
                          horizontal: "left",
                          vertical: "top",
                        }}
                        sx={{ marginTop: 1 }}
                      >
                        <label htmlFor="file-upload">
                          <MenuItem
                            onClick={() => {
                              handleCloseMenu();
                            }}
                            itemProp=""
                            sx={{
                              ":hover": {
                                color: colorStyling.white.hover,
                                backgroundColor: colorStyling.primary,
                              },
                            }}
                          >
                            <TypographyNormal sx={{ ml: 1, color: "inherit" }}>
                              {t("setting.placeholderUploadNew")}
                            </TypographyNormal>
                          </MenuItem>
                        </label>

                        <MenuItem
                          onClick={() => {
                            handleOpenMediaPicker();
                            handleCloseMenu();
                          }}
                          sx={{
                            ":hover": {
                              color: colorStyling.white.hover,
                              backgroundColor: colorStyling.primary,
                            },
                          }}
                        >
                          <TypographyNormal sx={{ ml: 1, color: "inherit" }}>
                            {t("setting.placeholderExistingMedia")}
                          </TypographyNormal>
                        </MenuItem>
                      </Menu>
                    </Box>

                    <TextField
                      variant="outlined"
                      value={adPlaceholderFile.name ?? ""}
                      placeholder={t("setting.placeholderPlaceholder")}
                      disabled
                      sx={{
                        // for future reference
                        "& .MuiOutlinedInput-root": {},
                        "& .MuiInputBase-input.Mui-disabled": {
                          py: 0,
                          height: "40px",
                          WebkitTextFillColor: colorStyling.black,
                          backgroundColor: colorStyling.white.inactive,
                          border: errNoPlaceholder && "1px solid red",
                        },
                      }}
                    />

                    <Box
                      display={"flex"}
                      gap={2}
                      alignItems={"center"}
                      sx={{ width: "123px", ml: 2 }}
                    >
                      {!showThumbnailButton && (
                        <>
                          {!uploadingMedia[adPlaceholder.id] ? (
                            <Box>
                              {adPlaceholderFile?.name && (
                                <Button
                                  variant="contained"
                                  sx={{ ml: 2, bgcolor: "#1976d2" }}
                                  onClick={uploadNewMedia}
                                >
                                  {tCommon("uploadBtn")}
                                </Button>
                              )}
                            </Box>
                          ) : (
                            <Box
                              width={60}
                              minHeight={5}
                              sx={{
                                mt: "2px",
                                position: "relative",
                              }}
                            >
                              {uploadingMedia[adPlaceholder.id].status ===
                              "queued" ? (
                                <LinearProgress
                                  sx={{
                                    height: 5,
                                    borderRadius: "11px",
                                  }}
                                  variant="query"
                                />
                              ) : (
                                <LinearProgress
                                  sx={{
                                    height: 5,
                                    borderRadius: "11px",
                                  }}
                                  variant="determinate"
                                  value={
                                    uploadingMedia[adPlaceholder.id].progress
                                  }
                                />
                              )}

                              {uploadingMedia[adPlaceholder.id].status ===
                                "completed" && (
                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                  sx={{
                                    p: "1px",
                                    top: "-16px",
                                    right: "-11px",
                                    position: "absolute",
                                    borderRadius: "999px",
                                    backgroundColor: "powderblue",
                                  }}
                                >
                                  <CheckIcon fontSize="small" />
                                </Box>
                              )}

                              {/* // TODO: handling error upon upload */}
                              {/* <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              sx={{
                                p: "1px",
                                top: "-16px",
                                right: "-11px",
                                position: "absolute",
                                borderRadius: "999px",
                                backgroundColor: "orangered",
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </Box> */}
                            </Box>
                          )}
                        </>
                      )}

                      {showThumbnailButton && (
                        <Button
                          variant="outlined"
                          fullWidth
                          startIcon={<PreviewIcon />}
                          onClick={() => setShowThumbnailFull(true)}
                          sx={{
                            mr: 1,
                            color: "#1976d2",
                            borderColor: "#1976d2",
                            backgroundColor: "white",
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                            },
                          }}
                        >
                          {tCommon("previewBtn")}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

                {errNoPlaceholder && (
                  <TypographySmall
                    sx={{
                      ml: 1,
                      mt: "2px",
                      color: "red",
                      fontWeight: 600,
                    }}
                  >
                    {errNotUploaded
                      ? t("setting.errNoPlaceholderNotUploaded")
                      : t("setting.errNoPlaceholderNoFile")}
                  </TypographySmall>
                )}

                {errUploadPlaceholder && (
                  <TypographySmall
                    sx={{
                      ml: 1,
                      mt: "2px",
                      color: "red",
                      fontWeight: 600,
                    }}
                  >
                    {t("setting.errUploadPlaceholder")}
                  </TypographySmall>
                )}
              </Box>
            </Box>

            {!adPlaceholderCheck && (
              <Box
                sx={{ width: "100%", height: "11px", position: "relative" }}
                display={"flex"}
                justifyContent={"end"}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={noPlaceholderCheckbox}
                      onClick={(e) => {
                        setNoPlaceholderCheckbox(e.target.checked);
                      }}
                      sx={{
                        p: "4px",
                        background: noPlaceholderAnimating ? "pink" : undefined,
                        transition: "background 0.4s ease-in-out",
                      }}
                    />
                  }
                  label={
                    <TypographySmall
                      sx={{
                        color: "red",
                        fontWeight: noPlaceholderAnimating ? "bold" : "normal",
                        transition: "font-weight 0.4s ease-in-out",
                      }}
                    >
                      {parser(t("setting.noPlaceholderCheckbox"))}
                    </TypographySmall>
                  }
                  sx={{ m: 0, position: "absolute", marginTop: "-4px" }}
                />
              </Box>
            )}
          </DialogContent>
        )}

        <DialogActions>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<ClearIcon />}
            disabled={disableButtonFlag}
            onClick={handleCancelDialog}
          >
            {tCommon("cancelBtn")}
          </Button>

          <Button
            variant="contained"
            fullWidth
            startIcon={<DoneIcon />}
            disabled={disableButtonFlag}
            onClick={submit}
          >
            {tCommon("submitBtn")}
          </Button>
        </DialogActions>

        <Dialog
          open={showThumbnailFull}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown={true}
          onClose={() => setShowThumbnailFull(false)}
          PaperProps={{ sx: { backgroundColor: "black" } }}
        >
          {previewPlaceholder}
        </Dialog>

        <Dialog maxWidth="sm" open={openCancelDialog}>
          <DialogContent>
            <DialogContentText
              sx={{
                mb: 2,
                textAlign: "center",
              }}
            >
              {parser(t("setting.cancelConfirmationText"))}
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCloseCancelDialog}
            >
              {tCommon("noBtn")}
            </Button>

            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                handleCloseCancelDialog();
                handleClose();
              }}
            >
              {tCommon("yesBtn")}
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    </>
  );
};

export default AdslotSetting;
